import moment from 'moment'
import { ActionGoalKind, ActionGoalOption, ActionGoalSummary, Maybe } from '@src/types'
import { DATE_FORMAT } from '@src/config/momentFormat'
import { CATEGORY_ORDER } from './constants'

export const sortSummariesByCategory = (summaries: ActionGoalSummary[]) => {
  const sortedSummaries = summaries.slice().sort((a, b) => {
    // Request from product - always show Video Call Presence goal at the top
    const videoCallPresenceComparison = compareVideoCallPresenceGoal(
      a.actionGoal.kind,
      b.actionGoal.kind,
    )
    if (videoCallPresenceComparison !== 0) {
      return videoCallPresenceComparison
    }

    // 1. Sort by category using explicit order
    const categoryDiff =
      CATEGORY_ORDER.indexOf(a.actionGoal.category) - CATEGORY_ORDER.indexOf(b.actionGoal.category)

    if (categoryDiff !== 0) {
      return categoryDiff
    }

    // 2. Same category → sort alphabetically by kind
    return a.actionGoal.kind.localeCompare(b.actionGoal.kind)
  })

  return sortedSummaries
}

export const sortActionGoalsByCategory = (actionGoals: ActionGoalOption[]) => {
  const sortedGoals = actionGoals.slice().sort((a, b) => {
    // Request from product - always show Video Call Presence goal at the top
    const videoCallPresenceComparison = compareVideoCallPresenceGoal(a.kind, b.kind)
    if (videoCallPresenceComparison !== 0) {
      return videoCallPresenceComparison
    }

    // 1. Sort by category using explicit order
    const categoryDiff = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)

    if (categoryDiff !== 0) {
      return categoryDiff
    }

    // 2. Same category → sort alphabetically by kind
    return a.kind.localeCompare(b.kind)
  })

  return sortedGoals
}

const compareVideoCallPresenceGoal = (a: ActionGoalKind, b: ActionGoalKind) => {
  if (a === ActionGoalKind.VideoCallPresenceGoal && b !== ActionGoalKind.VideoCallPresenceGoal) {
    return -1
  }
  if (b === ActionGoalKind.VideoCallPresenceGoal && a !== ActionGoalKind.VideoCallPresenceGoal) {
    return 1
  }
  return 0
}

export const getDisplayUnitsChunk = (displayUnits: Maybe<string> | undefined) => {
  if (displayUnits === '%') {
    return '%'
  }
  if (!displayUnits) {
    return ''
  }
  return ` ${displayUnits}`
}

export const startOfWeekMoment = (date?: string) => {
  const workingDate = date ? moment(date) : moment()
  return workingDate.startOf('isoWeek')
}

export const startOfCurrentWeek = (): string => {
  return startOfWeekMoment().format(DATE_FORMAT)
}

export const endOfWeekMoment = (date?: string): moment.Moment => {
  const workingDate = date ? moment(date) : moment()
  return workingDate.endOf('isoWeek')
}

export const endOfCurrentWeek = (): string => {
  return nextSundayMoment().format(DATE_FORMAT)
}

export const previousMonday = (date: string): string => {
  const mondayFromDate = moment(date).startOf('isoWeek')

  const previousMoment = mondayFromDate.subtract(1, 'week')

  return previousMoment.format(DATE_FORMAT)
}

export const nextMonday = (date: string): string => {
  return nextMondayMoment(date).format(DATE_FORMAT)
}

export const nextMondayMoment = (date?: string): moment.Moment => {
  const workingDate = date ? moment(date) : moment()
  const mondayFromDate = moment(workingDate).startOf('isoWeek')
  const nextMoment = mondayFromDate.add(1, 'week')
  return nextMoment
}

export const nextSundayMoment = (date?: string): moment.Moment => {
  const workingDate = date ? moment(date) : moment()
  const sundayFromDate = workingDate.endOf('isoWeek')
  return sundayFromDate
}

export const calculateProgress = (completionsCount: number, numberDays: number): number => {
  return numberDays > 0 ? (completionsCount / numberDays) * 100 : 0
}
